import { Box, CircularProgress, Typography } from '@mui/material';
import Button from '@shared/components/Buttons/Button';
import { useQuestions } from '@shared/hooks/useQuestions';
import { useApi } from '@shared/services/api';
import { useSnackbar } from '@shared/services/SnackbarService';
import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { UserContext } from '@/shared/contexts/UserContext';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';
import { useReviewContext } from '../../review/context/ReviewContext';
import AskQuestionModal from './AskQuestionModal';
import QuestionCard from './QuestionCard';

interface QAPanelProps {
  reviewId: number;
  onAskQuestion?: () => void;
  onRefetchReady?: (refetch: () => void) => void;
  questionRefs?: React.MutableRefObject<{ [key: number]: HTMLDivElement | null }>;
  highlightQuestionId?: number;
}

const PanelContainer = styled(Box)`
  margin: 48px 0;
`;

const PanelHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 2px solid rgba(11, 116, 218, 0.12);
`;

const HeaderIcon = styled(Box)`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: linear-gradient(135deg, #0b74da 0%, #78bbf1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  box-shadow: 0 4px 16px rgba(11, 116, 218, 0.25);
`;

const HeaderContent = styled(Box)`
  flex: 1;
`;

const Title = styled(Typography)`
  font-size: 1.75rem;
  font-weight: 700;
  background: linear-gradient(135deg, #0b74da 0%, #78bbf1 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 4px;
`;

const Subtitle = styled(Typography)`
  font-size: 0.9375rem;
  color: rgba(0, 0, 0, 0.6);
`;

const QuestionCount = styled(Box)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0 12px;
  border-radius: 20px;
  background: linear-gradient(135deg, rgba(11, 116, 218, 0.1) 0%, rgba(120, 187, 241, 0.08) 100%);
  border: 2px solid rgba(11, 116, 218, 0.2);
  font-weight: 700;
  color: #0b74da;
  font-size: 1rem;
`;

const QuestionsList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const EmptyState = styled(Box)`
  text-align: center;
  padding: 80px 20px;
  background: linear-gradient(135deg, rgba(11, 116, 218, 0.02) 0%, rgba(120, 187, 241, 0.03) 100%);
  border-radius: 16px;
  border: 2px dashed rgba(11, 116, 218, 0.15);
`;

const EmptyIcon = styled(Box)`
  font-size: 64px;
  margin-bottom: 24px;
  opacity: 0.5;
`;

const EmptyTitle = styled(Typography)`
  font-size: 1.25rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
  margin: 0 auto 8px auto;
`;

const EmptyDescription = styled(Typography)`
  font-size: 0.9375rem;
  color: rgba(0, 0, 0, 0.5);
  max-width: 500px;
  margin: 0 auto;
  line-height: 1.6;
`;

const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 20px;
`;

const ErrorContainer = styled(Box)`
  padding: 40px 20px;
  text-align: center;
  background: rgba(211, 47, 47, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(211, 47, 47, 0.2);
`;

const ErrorText = styled(Typography)`
  color: #d32f2f;
  font-size: 0.9375rem;
`;

const QAPanel: React.FC<QAPanelProps> = ({
  reviewId,
  onAskQuestion,
  onRefetchReady,
  questionRefs,
  highlightQuestionId,
}) => {
  const { user } = useContext(UserContext);
  const { currentReview } = useReviewContext();
  const { questions, loading, error, refetch } = useQuestions(reviewId, !!user?.idToken);
  const { openMessage, closeMessage } = useMessageService();
  const { showSnackbar } = useSnackbar();
  const api = useApi();

  // Expose refetch to parent
  useEffect(() => {
    if (onRefetchReady) {
      onRefetchReady(refetch);
    }
  }, [onRefetchReady, refetch]);

  // Safety check to ensure questions is always an array
  const safeQuestions = Array.isArray(questions) ? questions : [];
  const answeredCount = safeQuestions.filter((q) => q.answer).length;

  const handleAnswerQuestion = (questionId: number) => {
    const question = safeQuestions.find((q) => q.id === questionId);
    if (!question) return;

    let answerText = '';
    let isAnswerValid = false;
    let setShowValidation: ((show: boolean) => void) | null = null;

    openMessage({
      modalType: 'action',
      header: 'Answer Question',
      customBody: (
        <AskQuestionModal
          mode="answer"
          reviewId={reviewId}
          companyName={currentReview?.companyName || ''}
          questionText={question.question}
          onSubmit={(text, showValidationSetter) => {
            answerText = text;
            isAnswerValid = text.trim().length >= 25;
            setShowValidation = showValidationSetter;
          }}
          onTextChange={(text, isValid, showValidationSetter) => {
            answerText = text;
            isAnswerValid = isValid;
            setShowValidation = showValidationSetter;
          }}
        />
      ),
      size: 'medium',
      confirmLabel: 'Submit Answer',
      cancelLabel: 'Cancel',
      confirmButtonColor: 'primary',
      onConfirm: async () => {
        if (isAnswerValid && answerText.trim()) {
          try {
            await api.post('/answer', {
              answer: answerText.trim(),
              questionId,
              reviewId,
            });
            showSnackbar('Answer submitted successfully!', 'success');
            closeMessage();
            refetch();
            return true;
          } catch {
            showSnackbar('Failed to submit answer. Please try again.', 'error');
            return false;
          }
        }
        if (setShowValidation) {
          setShowValidation(true);
        }
        return false;
      },
    });
  };

  return (
    <PanelContainer id="qa" data-testid="qa-panel">
      <PanelHeader>
        <HeaderIcon>💬</HeaderIcon>
        <HeaderContent>
          <Title>Questions & Answers</Title>
          <Subtitle>Community insights and expert responses about this deal review</Subtitle>
        </HeaderContent>
        {safeQuestions.length > 0 && (
          <QuestionCount>
            {answeredCount}/{safeQuestions.length}
          </QuestionCount>
        )}
      </PanelHeader>

      {loading && (
        <LoadingContainer>
          <CircularProgress size={48} sx={{ color: '#0b74da' }} />
        </LoadingContainer>
      )}

      {error && (
        <ErrorContainer>
          <ErrorText>{error}</ErrorText>
        </ErrorContainer>
      )}

      {!loading && !error && safeQuestions.length === 0 && (
        <EmptyState>
          <EmptyIcon>🤔</EmptyIcon>
          <EmptyTitle>No Questions Yet</EmptyTitle>
          <EmptyDescription>
            Be the first to ask a question about this deal review! Get insights from the deal team.
          </EmptyDescription>
          {onAskQuestion && (
            <Box sx={{ mt: 3 }}>
              <Button
                data-testid="ask-question-button"
                size="medium"
                variantType="success"
                onClick={onAskQuestion}
                sx={{
                  minWidth: '180px',
                  background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                  color: 'white',
                  fontWeight: 600,
                  padding: '12px 32px',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 24px rgba(76, 175, 80, 0.4)',
                  },
                }}
              >
                Ask a Question
              </Button>
            </Box>
          )}
        </EmptyState>
      )}

      {!loading && !error && safeQuestions.length > 0 && (
        <QuestionsList>
          {safeQuestions.map((question) => (
            <QuestionCard
              key={question.id}
              question={question}
              onAnswer={handleAnswerQuestion}
              ref={(el) => {
                if (questionRefs && el) {
                  questionRefs.current[question.id] = el;
                }
              }}
              shouldHighlight={highlightQuestionId === question.id}
            />
          ))}
        </QuestionsList>
      )}
    </PanelContainer>
  );
};

export default QAPanel;
